import {
  CSRF_TOKEN,
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '@topcon/topcon-utils';
import { PropsWithChildren } from 'react';
import { Navigate } from 'react-router-dom';

import { decideAccess } from '../app/routes/access';
import { AppRoutes } from '../app/routes/utils';
import { useAppService } from '../providers/app/app.context';
import { SessionType } from '../types';

interface AuthorizeProps {
  /** Route whose `routeAccess` rule guards the view; omitted = open to every session. */
  route?: AppRoutes;
}

/**
 * Route guard driven by the route's single access rule (ADR 0006).
 * - Roles or Business Partner still loading: renders nothing.
 * - Wrong session type: re-login into the required session.
 * - Denied on Roles or Business Partner conditions: redirect to Overview.
 */
function Authorize({ route, children }: PropsWithChildren<AuthorizeProps>) {
  removeLocalStorage(CSRF_TOKEN);

  const decision = decideAccess(route, useAppService());

  switch (decision.kind) {
    case 'allowed':
      return children ?? null;
    case 'loading':
      return null;
    case 'denied':
      return <Navigate to={AppRoutes.Overview} replace />;
    case 'wrongSession':
      window.location.replace(
        getLoginURL(decision.session, decision.session === SessionType.BusinessPartner)
      );
      return null;
  }
}

export const getLoginURL = (sessionType?: SessionType, goToRoot: boolean = true) => {
  console.debug('[getLoginURL] called with', {
    sessionType,
    sessionTypeType: typeof sessionType,
    SessionTypeBusinessPartner: SessionType.BusinessPartner,
    sessionTypeEqBP: sessionType === SessionType.BusinessPartner,
    goToRoot,
  });

  const csrfToken = getCsrfToken();

  const { pathname, search, hash } = window.location;
  const currentRelativeURL = goToRoot ? '/' : `${pathname}${search}${hash}`;
  const args = new URLSearchParams();
  args.set('state', csrfToken);
  args.set('redirect_url', currentRelativeURL);

  if (sessionType) {
    const switchVal = sessionType === SessionType.BusinessPartner ? 'bp' : 'nobp';
    args.set('switch', switchVal);
    console.debug('[getLoginURL] set switch arg', { switchVal });
  } else {
    console.debug('[getLoginURL] no sessionType, skip switch arg');
  }

  const url = `/api/auth?${args}`;
  console.debug('[getLoginURL] result URL', url);
  return url;
};

/**
 * Get a new CSRF token and put it on the list of acceptable tokens.
 * This allows for multiple URLS
 */
const getCsrfToken = () => {
  let csrfToken = getLocalStorage(CSRF_TOKEN);

  if (!csrfToken) {
    csrfToken = String(crypto.getRandomValues(new Uint32Array(1))[0]);
    setLocalStorage(CSRF_TOKEN, csrfToken);
  }

  return csrfToken;
};

export default Authorize;
